/**
 * 플레어 헤더 배압 계산 — 지선이 갈라지는 망 구조.
 *
 * 근거: API Standard 521, 2020년 6월판 5.5
 *   5.5.3 라인 사이징 · 5.5.4 다중 분출 · 5.5.5 등온 압력강하 · 5.5.9 부속
 *
 * ── 다루는 형태 ────────────────────────────────────────
 *
 *   밸브1 ┐
 *         ├─ 지선 A ─┐
 *   밸브2 ┘          │
 *                    ├─ 주헤더 ─▶ 플레어 팁 (압력을 안다)
 *   밸브3 ─ 지선 B ──┘
 *
 * 구간마다 **어느 구간으로 흘러 나가는지**(`downstreamId`)를 지정해
 * 트리를 만든다. `downstreamId` 가 null 인 구간이 팁에 직접 닿는다.
 * 1단계의 한 줄짜리 헤더는 이 구조의 특수한 경우다.
 *
 * ── 계산 방향 ──────────────────────────────────────────
 *
 * ① 유량: 잎(최상류)에서 뿌리(팁)로 더해 올라간다
 * ② 압력: **뿌리에서 잎으로** 거슬러 올라간다 (5.5.5)
 * ③ 밸브 배압: 붙어 있는 구간의 상류 끝 압력에서 테일파이프를 더 거슬러
 *
 * ⚠ 정압(액주)·운동에너지 회복은 다루지 않는다. 수평 배관 전제다.
 */
import { frictionFactor, reynolds } from "./pipeFlow";
import {
  mixStreams,
  solveInletPressure,
  type IsothermalSegmentResult,
  type StreamProperties,
} from "./isothermalFlow";

/** 헤더에 붙는 밸브 하나 — 배출 조건과 테일파이프. */
export interface FlareValveInput {
  readonly id: string;
  readonly tag: string;
  /** 이 시나리오에서 이 밸브가 흘리는 유량 (kg/h). 0 이면 분출하지 않는다. */
  readonly massFlowKgH: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
  /** 절대점도 (cP) — 마찰계수를 Re 로 구하는 데 쓴다 */
  readonly viscosityCp: number;
  readonly tailInnerDiameterM: number;
  /** 테일파이프 등가길이 (m) — 직관 + 부속 */
  readonly tailEquivalentLengthM: number;
  /** 테일파이프 절대조도 (m) */
  readonly tailRoughnessM: number;
  /** 붙는 헤더 구간 id. 그 구간의 **상류 끝**에서 합류한다. */
  readonly segmentId: string;
}

/** 헤더 구간 하나. */
export interface HeaderSegmentInput {
  readonly id: string;
  readonly label: string;
  readonly innerDiameterM: number;
  /** 등가길이 (m). 대기 방출이어도 출구 K=1 은 넣지 않는다(5.5.9). */
  readonly equivalentLengthM: number;
  /** 절대조도 (m). 마찰계수는 Re 와 함께 Colebrook 으로 구한다. */
  readonly roughnessM: number;
  /**
   * 이 구간이 흘러 나가는 하류 구간의 id.
   * **null 이면 플레어 팁에 직접 닿는다** — 그런 구간은 하나뿐이어야 한다.
   */
  readonly downstreamId: string | null;
}

export interface FlareHeaderInput {
  /** 플레어 팁(계통 출구) 절대압 (kPa). */
  readonly tipPressureKpaAbs: number;
  readonly valves: readonly FlareValveInput[];
  readonly segments: readonly HeaderSegmentInput[];
  /** 마하수 상한 — 표준이 값을 정하지 않으므로 사용자가 정한다(5.5.3). */
  readonly machLimit: number;
}

export interface SegmentResult {
  readonly id: string;
  readonly label: string;
  /** 이 구간을 흐르는 누적 유량 (kg/h) */
  readonly massFlowKgH: number;
  readonly innerDiameterM: number;
  readonly inletPressureKpaAbs: number;
  readonly outletPressureKpaAbs: number;
  readonly pressureDropKpa: number;
  readonly inletMach: number;
  readonly outletMach: number;
  readonly criticalPressureKpaAbs: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
  readonly machExceeded: boolean;
  /** 절대점도 (cP) — 혼합 결과 */
  readonly viscosityCp: number;
  /** 레이놀즈수 (KOSHA 식 5-9) */
  readonly reynolds: number;
  /** Moody 마찰계수 (KOSHA 식 5-10, Colebrook) */
  readonly frictionFactor: number;
  /** 팁까지 몇 구간을 더 지나야 하는가 (0 = 팁에 직접 닿음) */
  readonly depth: number;
}

export interface ValveResult {
  readonly id: string;
  readonly tag: string;
  readonly massFlowKgH: number;
  /** 합류점(테일파이프 하류) 절대압 (kPa) */
  readonly nodePressureKpaAbs: number;
  /** 이 밸브가 받는 총 배압 — 테일파이프 입구 = 밸브 출구 플랜지. */
  readonly backpressureKpaGauge: number;
  readonly backpressureKpaAbs: number;
  readonly tailDropKpa: number;
  readonly tailInletMach: number;
  readonly tailOutletMach: number;
  readonly machExceeded: boolean;
  readonly reynolds: number;
  readonly frictionFactor: number;
  readonly segmentId: string;
}

export interface FlareHeaderSuccess {
  readonly ok: true;
  /** 팁에 가까운 것부터 (depth 오름차순) */
  readonly segments: readonly SegmentResult[];
  readonly valves: readonly ValveResult[];
  readonly totalFlowKgH: number;
  readonly worstValve: ValveResult | null;
  readonly warnings: readonly string[];
}

export interface FlareHeaderFailure {
  readonly ok: false;
  readonly reason:
    | "invalid-input"
    | "choked"
    | "no-solution"
    | "invalid-topology";
  readonly message: string;
  readonly where?: string;
}

export type FlareHeaderResult = FlareHeaderSuccess | FlareHeaderFailure;

const ATM_KPA = 101.325;

function fail(
  reason: FlareHeaderFailure["reason"],
  message: string,
  where?: string,
): FlareHeaderFailure {
  return where === undefined
    ? { ok: false, reason, message }
    : { ok: false, reason, message, where };
}

function liftFailure(
  r: Extract<IsothermalSegmentResult, { ok: false }>,
  where: string,
): FlareHeaderFailure {
  return fail(r.reason, `${where}: ${r.message}`, where);
}

/* ── 망 구조 확인 ────────────────────────────────────────── */

export interface TopologyOrder {
  /** 팁 → 상류 순서 (BFS). 압력은 이 순서로 푼다. */
  readonly rootFirst: readonly HeaderSegmentInput[];
  /** 각 구간의 바로 위(상류) 구간들 */
  readonly upstreamOf: ReadonlyMap<string, readonly HeaderSegmentInput[]>;
  /** 팁까지의 거리 */
  readonly depthOf: ReadonlyMap<string, number>;
}

/**
 * 구간 연결이 트리를 이루는지 확인하고 계산 순서를 만든다.
 *
 * 막는 것:
 *  · 팁에 닿는 구간이 없거나 둘 이상
 *  · 없는 구간을 가리키는 연결
 *  · 순환(A→B→A) 이나 팁에 닿지 못하는 구간
 */
export function buildTopology(
  segments: readonly HeaderSegmentInput[],
): TopologyOrder | FlareHeaderFailure {
  if (segments.length === 0) {
    return fail("invalid-input", "헤더 구간이 하나도 없습니다.");
  }

  const byId = new Map<string, HeaderSegmentInput>();
  for (const s of segments) {
    if (byId.has(s.id)) {
      return fail("invalid-topology", `구간 id 가 중복됩니다: ${s.label}`, s.label);
    }
    byId.set(s.id, s);
  }

  const roots = segments.filter((s) => s.downstreamId === null);
  if (roots.length === 0) {
    return fail(
      "invalid-topology",
      "플레어 팁에 닿는 구간이 없습니다. 마지막 구간의 '흘러가는 곳'을 " +
        "'플레어 팁'으로 지정하십시오.",
    );
  }
  if (roots.length > 1) {
    return fail(
      "invalid-topology",
      `플레어 팁에 닿는 구간이 ${roots.length} 개입니다(${roots
        .map((r) => r.label)
        .join(", ")}). 팁은 하나뿐이어야 합니다.`,
    );
  }

  const upstreamOf = new Map<string, HeaderSegmentInput[]>();
  for (const s of segments) upstreamOf.set(s.id, []);
  for (const s of segments) {
    if (s.downstreamId === null) continue;
    const target = upstreamOf.get(s.downstreamId);
    if (target === undefined) {
      return fail(
        "invalid-topology",
        `'${s.label}' 이 없는 구간으로 연결되어 있습니다.`,
        s.label,
      );
    }
    target.push(s);
  }

  // 팁에서 상류로 훑는다. 다 못 닿으면 순환이거나 끊어진 것이다.
  const rootFirst: HeaderSegmentInput[] = [];
  const depthOf = new Map<string, number>();
  const queue: Array<{ seg: HeaderSegmentInput; depth: number }> = [
    { seg: roots[0]!, depth: 0 },
  ];
  while (queue.length > 0) {
    const { seg, depth } = queue.shift()!;
    if (depthOf.has(seg.id)) continue;
    depthOf.set(seg.id, depth);
    rootFirst.push(seg);
    for (const up of upstreamOf.get(seg.id) ?? []) {
      queue.push({ seg: up, depth: depth + 1 });
    }
  }

  if (rootFirst.length !== segments.length) {
    const stranded = segments.filter((s) => !depthOf.has(s.id));
    return fail(
      "invalid-topology",
      `플레어 팁까지 이어지지 않는 구간이 있습니다: ${stranded
        .map((s) => s.label)
        .join(", ")}. 연결이 서로를 가리키며 도는지(순환) 확인하십시오.`,
      stranded[0]?.label,
    );
  }

  return { rootFirst, upstreamOf, depthOf };
}

/* ── 풀이 ────────────────────────────────────────────────── */

export function solveFlareHeader(input: FlareHeaderInput): FlareHeaderResult {
  const { tipPressureKpaAbs: tipP, valves, segments, machLimit } = input;

  if (!Number.isFinite(tipP) || tipP <= 0) {
    return fail("invalid-input", "플레어 팁 압력은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(machLimit) || machLimit <= 0 || machLimit >= 1) {
    return fail(
      "invalid-input",
      "마하수 상한은 0 보다 크고 1 보다 작아야 합니다(등온 초크 한계가 1.0).",
    );
  }

  const topo = buildTopology(segments);
  if ("ok" in topo) return topo;

  const active = valves.filter((v) => v.massFlowKgH > 0);
  if (active.length === 0) {
    return fail(
      "invalid-input",
      "이 시나리오에서 분출하는 밸브가 없습니다. 유량을 확인하십시오.",
    );
  }
  const segIds = new Set(segments.map((s) => s.id));
  for (const v of active) {
    if (!segIds.has(v.segmentId)) {
      return fail(
        "invalid-input",
        `${v.tag} 가 없는 헤더 구간에 붙어 있습니다.`,
        v.tag,
      );
    }
  }

  const warnings: string[] = [];

  // ① 유량 — 잎에서 뿌리로 더해 올라간다.
  const ownStreams = new Map<string, StreamProperties[]>();
  const valvesOn = new Map<string, FlareValveInput[]>();
  for (const s of segments) {
    ownStreams.set(s.id, []);
    valvesOn.set(s.id, []);
  }
  for (const v of active) {
    ownStreams.get(v.segmentId)!.push({
      massFlowKgH: v.massFlowKgH,
      molecularWeight: v.molecularWeight,
      temperatureK: v.temperatureK,
      compressibility: v.compressibility,
      viscosityCp: v.viscosityCp,
    });
    valvesOn.get(v.segmentId)!.push(v);
  }

  const cumulative = new Map<string, StreamProperties[]>();
  // rootFirst 의 역순 = 상류부터. 상류를 먼저 채워야 하류에서 합칠 수 있다.
  for (let i = topo.rootFirst.length - 1; i >= 0; i -= 1) {
    const seg = topo.rootFirst[i]!;
    const fromUpstream = (topo.upstreamOf.get(seg.id) ?? []).flatMap(
      (u) => cumulative.get(u.id) ?? [],
    );
    cumulative.set(seg.id, [...fromUpstream, ...ownStreams.get(seg.id)!]);
  }

  // ② 압력 — 뿌리(팁)에서 상류로.
  const segResults = new Map<string, SegmentResult>();
  /** 구간의 하류 압력 = 하류 구간의 입구 압력. 뿌리는 팁 압력. */
  const downstreamPressure = new Map<string, number>();
  downstreamPressure.set(topo.rootFirst[0]!.id, tipP);

  for (const seg of topo.rootFirst) {
    const outletP = downstreamPressure.get(seg.id)!;
    const mixed = mixStreams(cumulative.get(seg.id)!);

    if (mixed === null) {
      // 이 가지로는 아무것도 흐르지 않는다 — 압력이 그대로 전달된다.
      segResults.set(seg.id, {
        id: seg.id,
        label: seg.label,
        massFlowKgH: 0,
        innerDiameterM: seg.innerDiameterM,
        inletPressureKpaAbs: outletP,
        outletPressureKpaAbs: outletP,
        pressureDropKpa: 0,
        inletMach: 0,
        outletMach: 0,
        criticalPressureKpaAbs: 0,
        molecularWeight: Number.NaN,
        temperatureK: Number.NaN,
        compressibility: Number.NaN,
        machExceeded: false,
        viscosityCp: Number.NaN,
        reynolds: Number.NaN,
        frictionFactor: Number.NaN,
        depth: topo.depthOf.get(seg.id)!,
      });
      for (const up of topo.upstreamOf.get(seg.id) ?? []) {
        downstreamPressure.set(up.id, outletP);
      }
      continue;
    }

    for (const w of mixed.warnings) {
      if (!warnings.includes(w)) warnings.push(w);
    }

    // KOSHA D-63 5.2.6.3~4: 상대조도와 Re 로 Colebrook 마찰계수를 구한다.
    // 점도는 cP → Pa·s (÷1000), 유량은 kg/h → kg/s (÷3600).
    const re = reynolds(
      mixed.massFlowKgH / 3600,
      seg.innerDiameterM,
      mixed.viscosityCp / 1000,
    );
    const f = frictionFactor(re, seg.roughnessM / seg.innerDiameterM);

    const solved = solveInletPressure({
      massFlowKgH: mixed.massFlowKgH,
      outletPressureKpaAbs: outletP,
      innerDiameterM: seg.innerDiameterM,
      equivalentLengthM: seg.equivalentLengthM,
      frictionFactor: f,
      compressibility: mixed.compressibility,
      temperatureK: mixed.temperatureK,
      molecularWeight: mixed.molecularWeight,
    });
    if (!solved.ok) return liftFailure(solved, `헤더 구간 '${seg.label}'`);

    segResults.set(seg.id, {
      id: seg.id,
      label: seg.label,
      massFlowKgH: mixed.massFlowKgH,
      innerDiameterM: seg.innerDiameterM,
      inletPressureKpaAbs: solved.inletPressureKpaAbs,
      outletPressureKpaAbs: outletP,
      pressureDropKpa: solved.pressureDropKpa,
      inletMach: solved.inletMach,
      outletMach: solved.outletMach,
      criticalPressureKpaAbs: solved.criticalPressureKpaAbs,
      molecularWeight: mixed.molecularWeight,
      temperatureK: mixed.temperatureK,
      compressibility: mixed.compressibility,
      machExceeded: solved.outletMach > machLimit,
      viscosityCp: mixed.viscosityCp,
      reynolds: re,
      frictionFactor: f,
      depth: topo.depthOf.get(seg.id)!,
    });

    // 상류 구간들은 모두 이 구간의 입구 압력에서 출발한다.
    for (const up of topo.upstreamOf.get(seg.id) ?? []) {
      downstreamPressure.set(up.id, solved.inletPressureKpaAbs);
    }
  }

  // ③ 밸브 배압.
  const valveResults: ValveResult[] = [];
  for (const seg of topo.rootFirst) {
    const nodeP = segResults.get(seg.id)!.inletPressureKpaAbs;
    for (const v of valvesOn.get(seg.id)!) {
      const tailRe = reynolds(
        v.massFlowKgH / 3600,
        v.tailInnerDiameterM,
        v.viscosityCp / 1000,
      );
      const tailF = frictionFactor(
        tailRe,
        v.tailRoughnessM / v.tailInnerDiameterM,
      );

      const solved = solveInletPressure({
        massFlowKgH: v.massFlowKgH,
        outletPressureKpaAbs: nodeP,
        innerDiameterM: v.tailInnerDiameterM,
        equivalentLengthM: v.tailEquivalentLengthM,
        frictionFactor: tailF,
        compressibility: v.compressibility,
        temperatureK: v.temperatureK,
        molecularWeight: v.molecularWeight,
      });
      if (!solved.ok) return liftFailure(solved, `테일파이프 '${v.tag}'`);

      valveResults.push({
        id: v.id,
        tag: v.tag,
        massFlowKgH: v.massFlowKgH,
        nodePressureKpaAbs: nodeP,
        backpressureKpaAbs: solved.inletPressureKpaAbs,
        backpressureKpaGauge: solved.inletPressureKpaAbs - ATM_KPA,
        tailDropKpa: solved.pressureDropKpa,
        tailInletMach: solved.inletMach,
        tailOutletMach: solved.outletMach,
        machExceeded: solved.outletMach > machLimit,
        reynolds: tailRe,
        frictionFactor: tailF,
        segmentId: v.segmentId,
      });
    }
  }

  const ordered = topo.rootFirst.map((s) => segResults.get(s.id)!);
  const totalFlowKgH = active.reduce((a, v) => a + v.massFlowKgH, 0);
  const worstValve =
    valveResults.length === 0
      ? null
      : valveResults.reduce((a, b) =>
          b.backpressureKpaAbs > a.backpressureKpaAbs ? b : a,
        );

  if (ordered.some((s) => s.machExceeded)) {
    warnings.push(
      `마하수가 지정한 상한 ${machLimit} 을 넘는 헤더 구간이 있습니다. ` +
        "표준은 값을 정하지 않고 사용자가 기준을 세우도록 합니다(5.5.3). " +
        "관경을 키우면 소음·진동과 압력강하가 함께 줄어듭니다.",
    );
  }
  if (valveResults.some((v) => v.machExceeded)) {
    warnings.push(
      "테일파이프 마하수가 상한을 넘습니다. 표준은 테일파이프 유속을 " +
        "**그 밸브 하나만 분출한다고 보고** 확인하라고 합니다(5.5.3).",
    );
  }

  return {
    ok: true,
    segments: ordered,
    valves: valveResults,
    totalFlowKgH,
    worstValve,
    warnings,
  };
}
